import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Autocomplete,
  Select,
  MenuItem
} from '@mui/material';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { CustomBreadcrumbs, PageBanner, TabMenu } from '../../common';
import profileService from '../../../infrastructure/services/profileService';
import studentProfileBg from '../../../assets/images/student-profile-bg.png';
import settingsBannerBg from '../../../assets/images/setting.jpg';
import countriesData from '../../../assets/json/countries.json';
import { validatePhoneNumber, getPhoneMaxLength } from '../../../infrastructure/utils/validation';
import { locationService } from '../../../infrastructure/services/locationService';

export default function SettingsProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const originalMain = useRef({ fullName: '', email: '', district: '', province: '' });
  const originalParent = useRef({ parentName: '', parentRelationship: '', parentPhone: '', parentEmail: '' });
  const [copied, setCopied] = useState(false);

  const getInitials = (name) => {
    if (!name) return 'UM';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Profile fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPhoto, setCurrentPhoto] = useState('');
  const [studentId, setStudentId] = useState('');
  const [role, setRole] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [birthday, setBirthday] = useState('');
  const [gender, setGender] = useState('');
  const [district, setDistrict] = useState('');
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');

  const [provincesList, setProvincesList] = useState([]);
  const [districtsList, setDistrictsList] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  useEffect(() => {
    const fetchProvinces = async () => {
      setLoadingProvinces(true);
      try {
        const data = await locationService.getProvinces();
        setProvincesList(data || []);
      } catch (err) {
        console.error('Failed to load provinces in Settings page:', err);
      } finally {
        setLoadingProvinces(false);
      }
    };
    fetchProvinces();
  }, []);

  useEffect(() => {
    const activeProvince = provincesList.find(p => p.en_US === province || p.name === province);
    if (activeProvince) {
      const fetchDistricts = async () => {
        setLoadingDistricts(true);
        try {
          const provId = activeProvince.index ?? activeProvince.id;
          const data = await locationService.getDistrictsByProvince(provId);
          setDistrictsList(data || []);
        } catch (err) {
          console.error('Failed to load districts in Settings page:', err);
        } finally {
          setLoadingDistricts(false);
        }
      };
      fetchDistricts();
    } else {
      setDistrictsList([]);
    }
  }, [province, provincesList]);

  // Parent Information fields
  const [parentName, setParentName] = useState('');
  const [parentRelationship, setParentRelationship] = useState('');
  const [parentCountryCode, setParentCountryCode] = useState('+94');
  const [parentLocalPhone, setParentLocalPhone] = useState('');
  const [parentEmail, setParentEmail] = useState('');

  // Helper to split parentPhone string into country code and local number
  const parsePhoneNumber = (phoneStr) => {
    const cleanPhone = (phoneStr || '').trim();
    const countryCodes = ['+94', '+91', '+1', '+44', '+61'];
    for (const code of countryCodes) {
      if (cleanPhone.startsWith(code)) {
        return {
          countryCode: code,
          localNumber: cleanPhone.substring(code.length)
        };
      }
    }
    return {
      countryCode: '+94',
      localNumber: cleanPhone
    };
  };

  // Saving state for each card separately
  const [isSavingParent, setIsSavingParent] = useState(false);

  // Local editing / preview states
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // UI status states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Settings' }
  ];

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const loadProfile = async () => {
    const profileData = await profileService.getProfile();
    const cleanEmail = (profileData.email || '').replace(/\s/g, '');
    const cleanParentEmail = (profileData.parentEmail || profileData.parent_email || '').replace(/\s/g, '');
    const cleanFullName = (profileData.fullName || profileData.displayName || '').trim().replace(/\s{2,}/g, ' ');
    const cleanParentName = (profileData.parentName || profileData.parent_name || '').trim().replace(/\s{2,}/g, ' ');
    const rawRole = profileData.role || '';
    const rawGender = profileData.gender || '';

    setFullName(cleanFullName);
    setEmail(cleanEmail);
    setCurrentPhoto(profileData.profileImage || '');
    setStudentId(profileData.ssoStudentId || '');
    setRole(rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : '');
    setPhoneNumber(profileData.phoneNumber || '');
    setBirthday(profileData.dateOfBirth || profileData.birthday || '');
    setGender(rawGender ? rawGender.charAt(0).toUpperCase() + rawGender.slice(1).toLowerCase() : '');
    setDistrict(profileData.district || '');
    setProvince(profileData.province || '');
    setCity(profileData.city || '');
    originalMain.current = {
      fullName: cleanFullName,
      email: cleanEmail,
      district: profileData.district || '',
      province: profileData.province || ''
    };
    const parsed = parsePhoneNumber(profileData.parentPhone || profileData.parent_phone || '');
    originalParent.current = {
      parentName: cleanParentName,
      parentRelationship: profileData.parentRelationship || profileData.parent_relationship || '',
      parentPhone: profileData.parentPhone || profileData.parent_phone || '',
      parentEmail: cleanParentEmail
    };
    setParentName(cleanParentName);
    setParentRelationship(profileData.parentRelationship || profileData.parent_relationship || '');
    setParentCountryCode(parsed.countryCode);
    setParentLocalPhone(parsed.localNumber);
    setParentEmail(cleanParentEmail);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        await loadProfile();
      } catch (err) {
        console.error('Failed to load profile details:', err);
        showToast(err.message || 'Failed to load profile settings.', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png'];
      const allowedExtensions = ['jpg', 'jpeg', 'png'];
      const extension = file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(extension);

      if (!isValidType) {
        showToast('Invalid file type. Only JPG, JPEG, and PNG images are allowed.', 'error');
        e.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast('File size must not exceed 5MB.', 'error');
        e.target.value = '';
        return;
      }

      setIsUploadingPhoto(true);
      try {
        const formData = new FormData();
        formData.append('full_name', fullName || 'Student');
        formData.append('profile_image', file);
        if (province && province.trim()) formData.append('province', province.trim());
        if (district && district.trim()) formData.append('district', district.trim());
        if (email && email.trim()) formData.append('email', email.trim());

        await profileService.updateProfile(formData);
        showToast('Profile picture updated successfully!', 'success');
        await loadProfile();
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      } catch (err) {
        console.error('Failed to upload profile picture:', err);
        showToast(err.message || 'Failed to upload profile picture.', 'error');
      } finally {
        setIsUploadingPhoto(false);
        e.target.value = '';
      }
    }
  };

  const handleCopyId = () => {
    if (studentId) {
      navigator.clipboard.writeText(studentId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Name field sanitizer: letters + spaces + apostrophe (') only, max 100 chars
  // Collapses multiple spaces and strips leading spaces.
  const sanitizeName = (value) => {
    let sanitized = value.replace(/[^a-zA-Z\s']/g, ''); // strip everything except letters, spaces, and '
    sanitized = sanitized.replace(/^\s+/, ''); // strip leading spaces
    sanitized = sanitized.replace(/\s{2,}/g, ' '); // collapse consecutive spaces
    return sanitized.slice(0, 100);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmedName = fullName.trim().replace(/\s{2,}/g, ' ');
    setFullName(trimmedName);
    if (!trimmedName) {
      showToast('Full name cannot be empty or whitespace only.', 'error');
      return;
    }
    if (trimmedName.length > 100) {
      showToast('Full name must be 100 characters or fewer.', 'error');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim() && !emailRegex.test(email.trim())) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }

    if (!province || !province.trim()) {
      showToast('Province is required.', 'error');
      return;
    }
    if (!district || !district.trim()) {
      showToast('District is required.', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('full_name', trimmedName);
      if (email.trim()) {
        formData.append('email', email.trim());
      }
      if (district && district.trim()) {
        formData.append('district', district.trim());
      }
      if (province && province.trim()) {
        formData.append('province', province.trim());
      }

      await profileService.updateProfile(formData);
      await loadProfile();

      showToast('Profile settings updated successfully!', 'success');
      window.dispatchEvent(new CustomEvent('profileUpdated'));
    } catch (err) {
      console.error('Failed to update student profile:', err);
      showToast(err.message || 'Error updating profile. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveParent = async () => {
    const trimmedParentName = parentName.trim().replace(/\s{2,}/g, ' ');
    setParentName(trimmedParentName);
    if (!trimmedParentName) {
      showToast('Parent name cannot be empty or whitespace only.', 'error');
      return;
    }
    if (trimmedParentName.length > 100) {
      showToast('Parent name must be 100 characters or fewer.', 'error');
      return;
    }

    const finalPhone = parentLocalPhone.trim() ? `${parentCountryCode}${parentLocalPhone.trim()}` : '';
    if (parentLocalPhone.trim()) {
      const phoneError = validatePhoneNumber(parentCountryCode, parentLocalPhone.trim());
      if (phoneError) {
        showToast(phoneError, 'error');
        return;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (parentEmail.trim() && !emailRegex.test(parentEmail.trim())) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }

    setIsSavingParent(true);
    try {
      const payload = {
        parent_name: trimmedParentName
      };
      const trimmedRelationship = parentRelationship.trim();
      if (trimmedRelationship) payload.parent_relationship = trimmedRelationship;
      if (finalPhone) payload.parent_phone = finalPhone;
      const trimmedEmail = parentEmail.trim();
      if (trimmedEmail) payload.parent_email = trimmedEmail;
      await profileService.updateParentDetails(payload);
      await loadProfile();
      showToast('Parent details updated successfully!', 'success');
    } catch (err) {
      console.error('Failed to update parent details:', err);
      showToast(err.message || 'Error updating parent details. Please try again.', 'error');
    } finally {
      setIsSavingParent(false);
    }
  };

  const disabledFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      height: '46px',
      backgroundColor: '#ffffff',
      '& fieldset': { borderColor: '#cbd5e1' },
      '& input': {
        color: '#475569',
        fontFamily: 'Poppins',
        fontSize: '13.5px',
        WebkitTextFillColor: '#475569'
      }
    }
  };

  const editableFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      height: '46px',
      backgroundColor: '#ffffff',
      '& fieldset': { borderColor: '#cbd5e1' },
      '&:hover fieldset': { borderColor: '#94a3b8' },
      '&.Mui-focused fieldset': { borderColor: '#FF502D' },
      '& input': {
        color: '#1e293b',
        fontFamily: 'Poppins',
        fontSize: '13.5px'
      }
    }
  };

  // ims-admin label style: bold dark label
  const SectionLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', fontFamily: 'Poppins', mb: 1 }}>
      {children}
    </Typography>
  );

  // Read-only label: same bold style, slightly muted
  const ReadOnlyLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', fontFamily: 'Poppins', mb: 1 }}>
      {children}
    </Typography>
  );

  // Info row used in the profile card
  const InfoRow = ({ icon, text, fallback }) => {
    const display = text || fallback;
    const isFallback = !text;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ color: '#64748b', display: 'flex', flexShrink: 0 }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: '15px',
            fontFamily: 'Poppins',
            fontWeight: isFallback ? 400 : 600,
            color: isFallback ? '#b0bec5' : '#1e293b',
            fontStyle: isFallback ? 'italic' : 'normal',
            lineHeight: 1.4,
            letterSpacing: '-0.1px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {display}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
      {/* Breadcrumbs */}
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />

      {/* Page Banner */}
      <PageBanner
        title="Settings"
        description="Manage your profile details, change contact email, or upload a new profile picture."
        backgroundImage={settingsBannerBg}
      />

      {/* Reusable Tab Switcher */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
        <TabMenu
          tabs={[
            { id: 'profile', label: 'Profile', icon: <PersonRoundedIcon /> },
            { id: 'notifications', label: 'Notification', icon: <NotificationsRoundedIcon /> }
          ]}
          activeTab="profile"
          onChange={(tabId) => navigate(tabId === 'profile' ? '/settings' : '/settings/notifications')}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#FF502D' }} />
        </Box>
      ) : (
        <form onSubmit={handleSave}>
          <Stack spacing={3} sx={{ mt: 4 }}>

            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                backgroundImage: `url(${studentProfileBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '16px',
                border: '2px solid #137ECA3D',
                overflow: 'hidden',
              }}
            >
              {/* White overlay so bg pattern is subtle */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none'
                }}
              />

              {/* Content */}
              <Box
                sx={{
                  position: 'relative',
                  p: { xs: 2.5, sm: 3 },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'center', sm: 'center' },
                  gap: { xs: 2, sm: 3 }
                }}
              >
                {/* Student ID badge — top right */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '10px',
                    px: 1.5,
                    py: 0.5
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', fontFamily: 'Poppins', letterSpacing: 0.3 }}>
                    {studentId || 'No ID'}
                  </Typography>
                  <Tooltip title={copied ? 'Copied!' : 'Copy ID'} placement="top">
                    <IconButton size="small" onClick={handleCopyId} sx={{ p: 0.25, color: '#94a3b8', '&:hover': { color: '#FF502D' } }}>
                      <ContentCopyRoundedIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Avatar + Upload button */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Box
                    sx={{ position: 'relative', cursor: 'pointer' }}
                    onClick={handleAvatarClick}
                  >
                    <Avatar
                      src={currentPhoto || undefined}
                      sx={{
                        width: { xs: 80, sm: 96 },
                        height: { xs: 80, sm: 96 },
                        borderRadius: '18px',
                        border: '3px solid #ffffff',
                        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
                        bgcolor: '#f1f5f9',
                        color: '#64748b',
                        fontWeight: 700,
                        fontSize: '26px',
                        fontFamily: 'Poppins',
                        transition: 'all 0.2s',
                        '&:hover': { opacity: 0.85 }
                      }}
                    >
                      {!currentPhoto && getInitials(fullName)}
                    </Avatar>
                    {isUploadingPhoto && (
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: 'rgba(255, 255, 255, 0.7)',
                        borderRadius: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                      }}>
                        <CircularProgress size={24} sx={{ color: '#FF502D' }} />
                      </Box>
                    )}
                    {/* Camera overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -6,
                        right: -6,
                        bgcolor: '#FF502D',
                        color: '#fff',
                        borderRadius: '50%',
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2.5px solid #fff',
                        boxShadow: '0 2px 8px rgba(255,80,45,0.4)'
                      }}
                    >
                      <PhotoCameraRoundedIcon sx={{ fontSize: '14px' }} />
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    color="inherit"
                    onClick={handleAvatarClick}
                    disabled={isUploadingPhoto}
                    sx={{
                      borderColor: '#cbd5e1',
                      color: '#475569',
                      fontWeight: 600,
                      fontFamily: 'Poppins',
                      fontSize: '11px',
                      textTransform: 'none',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.3,
                      mt: 0.5,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: '#94a3b8',
                        bgcolor: '#f8fafc'
                      }
                    }}
                  >
                    {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    style={{ display: 'none' }}
                  />
                </Box>

                {/* Name + Info details */}
                <Box sx={{ flex: 1, minWidth: 0, pt: { xs: 0, sm: 0 } }}>
                  {/* Name */}
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '20px', sm: '22px' },
                      color: '#1e293b',
                      fontFamily: 'Poppins',
                      mb: 1.5,
                      pr: { xs: 0, sm: '130px' } // leave room for SID badge on sm+
                    }}
                  >
                    {fullName || 'Student Name'}
                  </Typography>

                  {/* Two-column info grid */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: { xs: 0.8, sm: 1 },
                      rowGap: { xs: 0.8, sm: 1 }
                    }}
                  >
                    <InfoRow
                      icon={<BadgeRoundedIcon sx={{ fontSize: '16px' }} />}
                      text={role}
                      fallback="Grade not specified"
                    />
                    <InfoRow
                      icon={<CakeRoundedIcon sx={{ fontSize: '16px' }} />}
                      text={birthday}
                      fallback="Birthday not set"
                    />
                    <InfoRow
                      icon={<EmailRoundedIcon sx={{ fontSize: '16px' }} />}
                      text={email}
                      fallback="Email not set"
                    />
                    <InfoRow
                      icon={<PhoneRoundedIcon sx={{ fontSize: '16px' }} />}
                      text={phoneNumber}
                      fallback="Phone not provided"
                    />
                    <InfoRow
                      icon={<LocationOnRoundedIcon sx={{ fontSize: '16px' }} />}
                      text={[district, province].filter(Boolean).join(', ') || city || null}
                      fallback="Address not provided"
                    />
                    <InfoRow
                      icon={<PersonRoundedIcon sx={{ fontSize: '16px' }} />}
                      text={gender}
                      fallback="Gender not specified"
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* ─── Editable: Main Info Fields ───────────────────────── */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                border: '1px solid #e2e8f0',
                borderRadius: '24px',
                bgcolor: '#ffffff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 32, height: 32, borderRadius: '10px',
                    bgcolor: 'rgba(255,80,45,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <PersonRoundedIcon sx={{ fontSize: '18px', color: '#FF502D' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins' }}>
                    Main Information
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'Poppins' }}>
                    Update your personal details and profile photo.
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 3, borderColor: '#f1f5f9' }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 3
                }}
              >
                <Box>
                  <SectionLabel>Full Name</SectionLabel>
                  <TextField
                    fullWidth
                    value={fullName}
                    onChange={(e) => setFullName(sanitizeName(e.target.value))}
                    placeholder="Enter your full name"
                    inputProps={{ maxLength: 100 }}
                    sx={editableFieldSx}
                  />
                </Box>
                <Box>
                  <SectionLabel>Email Address</SectionLabel>
                  <TextField
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))}
                    placeholder="student@example.com"
                    sx={editableFieldSx}
                  />
                </Box>
                <Box>
                  <SectionLabel>Province</SectionLabel>
                  <Autocomplete
                    options={provincesList}
                    getOptionLabel={(option) => option.en_US && option.si_LK ? `${option.en_US} (${option.si_LK})` : option.en_US || option.name || ''}
                    isOptionEqualToValue={(option, val) => (option.index ?? option.id) === (val.index ?? val.id)}
                    value={provincesList.find(p => p.en_US === province || p.name === province) || null}
                    onChange={(_, newVal) => {
                      setProvince(newVal ? (newVal.en_US || newVal.name) : '');
                      setDistrict(''); // clear district when province changes
                    }}
                    loading={loadingProvinces}
                    disableClearable={!province}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select your province"
                        sx={editableFieldSx}
                        InputProps={{
                          ...(params.InputProps || {}),
                          endAdornment: (
                            <>
                              {loadingProvinces ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 0.5,
                          borderRadius: '16px',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                          border: '1px solid #f1f5f9',
                          overflow: 'hidden',
                          '& .MuiAutocomplete-listbox': {
                            p: '6px',
                            maxHeight: '280px',
                            '& .MuiAutocomplete-option': {
                              borderRadius: '10px',
                              fontFamily: 'Poppins',
                              fontSize: '13.5px',
                              color: '#1e293b',
                              fontWeight: 500,
                              px: 2,
                              py: '10px',
                              mb: '2px',
                              '&:hover': { bgcolor: '#fff4f2' },
                              '&[aria-selected="true"]': {
                                bgcolor: '#fff4f2',
                                color: '#FF502D',
                                fontWeight: 600
                              },
                              '&[aria-selected="true"]:hover': { bgcolor: '#ffe8e3' }
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
                <Box>
                  <SectionLabel>District</SectionLabel>
                  <Autocomplete
                    options={districtsList}
                    getOptionLabel={(option) => option.en_US && option.si_LK ? `${option.en_US} (${option.si_LK})` : option.en_US || option.name || ''}
                    isOptionEqualToValue={(option, val) => (option.index ?? option.id) === (val.index ?? val.id)}
                    disabled={!province}
                    value={districtsList.find(d => d.en_US === district || d.name === district) || null}
                    onChange={(_, newVal) => setDistrict(newVal ? (newVal.en_US || newVal.name) : '')}
                    loading={loadingDistricts}
                    disableClearable={!district}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={province ? (loadingDistricts ? "Loading districts..." : `Select district in ${province}`) : 'Select your district'}
                        sx={editableFieldSx}
                        InputProps={{
                          ...(params.InputProps || {}),
                          endAdornment: (
                            <>
                              {loadingDistricts ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps?.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 0.5,
                          borderRadius: '16px',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                          border: '1px solid #f1f5f9',
                          overflow: 'hidden',
                          '& .MuiAutocomplete-listbox': {
                            p: '6px',
                            maxHeight: '280px',
                            '& .MuiAutocomplete-option': {
                              borderRadius: '10px',
                              fontFamily: 'Poppins',
                              fontSize: '13.5px',
                              color: '#1e293b',
                              fontWeight: 500,
                              px: 2,
                              py: '10px',
                              mb: '2px',
                              '&:hover': { bgcolor: '#fff4f2' },
                              '&[aria-selected="true"]': {
                                bgcolor: '#fff4f2',
                                color: '#FF502D',
                                fontWeight: 600
                              },
                              '&[aria-selected="true"]:hover': { bgcolor: '#ffe8e3' }
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Box>

              {(() => {
                const isMainDirty =
                  fullName !== originalMain.current.fullName ||
                  email !== originalMain.current.email ||
                  district !== originalMain.current.district ||
                  province !== originalMain.current.province;
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSaving || !isMainDirty || !province || !district}
                      sx={{
                        bgcolor: '#FF502D', color: '#ffffff', fontWeight: 600,
                        fontFamily: 'Poppins', fontSize: '13px', textTransform: 'none',
                        borderRadius: '10px', px: 4, height: '40px',
                        boxShadow: '0 4px 14px rgba(255,80,45,0.35)',
                        '&:hover': { bgcolor: '#e64525', boxShadow: '0 6px 18px rgba(255,80,45,0.4)' },
                        '&:disabled': { bgcolor: '#cbd5e1', color: '#94a3b8', boxShadow: 'none' }
                      }}
                    >
                      {isSaving ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : 'Save Changes'}
                    </Button>
                  </Box>
                );
              })()}
            </Paper>

            {/* ─── Card 2: Parent / Guardian Information ────────────── */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                border: '1px solid #e2e8f0',
                borderRadius: '24px',
                bgcolor: '#ffffff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 32, height: 32, borderRadius: '10px',
                    bgcolor: 'rgba(99,102,241,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <FamilyRestroomRoundedIcon sx={{ fontSize: '18px', color: '#6366f1' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins' }}>
                    Parent / Guardian Information
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'Poppins' }}>
                    Update parent or guardian contact information.
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 3, borderColor: '#f1f5f9' }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 3
                }}
              >
                <Box>
                  <SectionLabel>Parent's Full Name</SectionLabel>
                  <TextField
                    fullWidth
                    value={parentName}
                    onChange={(e) => setParentName(sanitizeName(e.target.value))}
                    placeholder="Enter parent's full name"
                    inputProps={{ maxLength: 100 }}
                    sx={editableFieldSx}
                  />
                </Box>
                <Box>
                  <SectionLabel>Relationship</SectionLabel>
                  <TextField
                    select
                    fullWidth
                    value={parentRelationship || ''}
                    onChange={(e) => setParentRelationship(e.target.value)}
                    sx={{
                      ...editableFieldSx,
                      '& .MuiSelect-select': {
                        fontFamily: 'Poppins',
                        fontSize: '13.5px',
                        color: parentRelationship ? '#1e293b' : '#94a3b8',
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (val) => {
                        if (!val) {
                          return 'Select relationship';
                        }
                        return val;
                      },
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            mt: 0.5,
                            borderRadius: '16px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                            border: '1px solid #f1f5f9',
                            overflow: 'hidden',
                            '& .MuiList-root': {
                              p: '6px',
                              '& .MuiMenuItem-root': {
                                borderRadius: '10px',
                                fontFamily: 'Poppins',
                                fontSize: '13.5px',
                                color: '#1e293b',
                                fontWeight: 500,
                                px: 2,
                                py: '10px',
                                mb: '2px',
                                '&:hover': { bgcolor: '#fff4f2' },
                                '&.Mui-selected': {
                                  bgcolor: '#fff4f2',
                                  color: '#FF502D',
                                  fontWeight: 600
                                },
                                '&.Mui-selected:hover': { bgcolor: '#ffe8e3' }
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="" disabled sx={{ display: 'none' }}>
                      Select relationship
                    </MenuItem>
                    {Array.from(new Set(['Father', 'Mother', 'Guardian', parentRelationship].filter(Boolean))).map((rel) => (
                      <MenuItem key={rel} value={rel}>
                        {rel}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  <SectionLabel>Parent's Phone Number</SectionLabel>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', width: '100%' }}>
                    {/* Country code selector with real flags */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '12px',
                        backgroundColor: '#ffffff',
                        minWidth: '85px',
                        maxWidth: '90px',
                        px: 1,
                        justifyContent: 'center',
                        transition: 'all 0.25s ease',
                        '&:focus-within': {
                          borderColor: '#ff502d',
                          boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                        }
                      }}
                    >
                      <Select
                        value={parentCountryCode}
                        onChange={(e) => {
                          const newCode = e.target.value;
                          const maxLen = getPhoneMaxLength(newCode);
                          setParentCountryCode(newCode);
                          setParentLocalPhone((prev) => prev.slice(0, maxLen));
                        }}
                        variant="standard"
                        disableUnderline
                        IconComponent={() => (
                          <svg width="8" height="5" viewBox="0 0 10 6" fill="none" style={{ marginLeft: '4px' }}>
                            <path d="M1 1L5 5L9 1" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        renderValue={(selectedCode) => {
                          const c = countriesData.find(cnt => cnt.code === selectedCode) || countriesData[0];
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                component="img"
                                src={`https://flagcdn.com/w40/${c.iso}.png`}
                                alt={c.label}
                                sx={{
                                  width: '18px',
                                  height: 'auto',
                                  borderRadius: '2px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                              />
                              <Typography sx={{ fontSize: '13px', fontWeight: 500, fontFamily: 'Poppins' }}>
                                {c.code}
                              </Typography>
                            </Box>
                          );
                        }}
                        sx={{
                          width: '100%',
                          height: '100%',
                          fontFamily: 'Poppins',
                          fontSize: '13px',
                          color: '#334155',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            paddingLeft: '0px !important',
                            paddingRight: '16px !important',
                            background: 'none',
                            '&:focus': {
                              background: 'none'
                            }
                          }
                        }}
                      >
                        {countriesData.map((c) => (
                          <MenuItem
                            key={c.code}
                            value={c.code}
                            sx={{
                              fontFamily: 'Poppins',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              py: 0.75
                            }}
                          >
                            <Box
                              component="img"
                              src={`https://flagcdn.com/w40/${c.iso}.png`}
                              alt={c.label}
                              sx={{
                                width: '18px',
                                height: 'auto',
                                borderRadius: '2px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                              }}
                            />
                            <Typography sx={{ fontSize: '13px', fontWeight: 500, fontFamily: 'Poppins' }}>
                              {c.label} ({c.code})
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {/* Local Mobile Number Input Box */}
                    <TextField
                      fullWidth
                      value={parentLocalPhone}
                      onChange={(e) => setParentLocalPhone(e.target.value.replace(/\D/g, '').slice(0, getPhoneMaxLength(parentCountryCode)))}
                      placeholder="Parent's phone number"
                      sx={editableFieldSx}
                    />
                  </Box>
                </Box>
                <Box>
                  <SectionLabel>Parent's Email Address</SectionLabel>
                  <TextField
                    fullWidth
                    value={parentEmail}
                    onChange={(e) => setParentEmail(e.target.value.replace(/\s/g, ''))}
                    placeholder="parent@example.com"
                    sx={editableFieldSx}
                  />
                </Box>
              </Box>

              {/* Parent Save Button */}
              {(() => {
                const currentCombinedPhone = parentLocalPhone.trim() ? `${parentCountryCode}${parentLocalPhone.trim()}` : '';
                const isParentDirty =
                  parentName !== originalParent.current.parentName ||
                  parentRelationship !== originalParent.current.parentRelationship ||
                  currentCombinedPhone !== originalParent.current.parentPhone ||
                  parentEmail !== originalParent.current.parentEmail;
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveParent}
                      disabled={isSavingParent || !isParentDirty}
                      sx={{
                        bgcolor: '#FF502D', color: '#ffffff', fontWeight: 600,
                        fontFamily: 'Poppins', fontSize: '13px', textTransform: 'none',
                        borderRadius: '10px', px: 4, height: '40px',
                        boxShadow: '0 4px 14px rgba(255,80,45,0.35)',
                        '&:hover': { bgcolor: '#e64525', boxShadow: '0 6px 18px rgba(255,80,45,0.4)' },
                        '&:disabled': { bgcolor: '#cbd5e1', color: '#94a3b8', boxShadow: 'none' }
                      }}
                    >
                      {isSavingParent ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : 'Save Parent Details'}
                    </Button>
                  </Box>
                );
              })()}
            </Paper>

            {/* ─── Card 3: Account Information (Read-Only) ──────────── */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                border: '1px solid #e2e8f0',
                borderRadius: '24px',
                bgcolor: '#ffffff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32, height: 32, borderRadius: '10px',
                      bgcolor: 'rgba(100,116,139,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >
                    <AdminPanelSettingsRoundedIcon sx={{ fontSize: '18px', color: '#64748b' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins' }}>
                      Account Information
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'Poppins' }}>
                      Managed by the school administrator.
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Read-Only"
                  size="small"
                  sx={{
                    bgcolor: '#f1f5f9', color: '#64748b',
                    fontFamily: 'Poppins', fontWeight: 600,
                    fontSize: '11px', borderRadius: '8px', height: '22px'
                  }}
                />
              </Box>
              <Divider sx={{ mb: 3, borderColor: '#f1f5f9' }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 3,
                  mb: 3
                }}
              >
                <Box>
                  <ReadOnlyLabel>Student ID</ReadOnlyLabel>
                  <TextField fullWidth disabled value={studentId} sx={disabledFieldSx} />
                </Box>
                <Box>
                  <ReadOnlyLabel>Role</ReadOnlyLabel>
                  <TextField fullWidth disabled value={role} sx={disabledFieldSx} />
                </Box>
                <Box>
                  <ReadOnlyLabel>Phone Number</ReadOnlyLabel>
                  <TextField fullWidth disabled value={phoneNumber} sx={disabledFieldSx} />
                </Box>
                <Box>
                  <ReadOnlyLabel>Date of Birth</ReadOnlyLabel>
                  <TextField fullWidth disabled value={birthday} sx={disabledFieldSx} />
                </Box>
                <Box>
                  <ReadOnlyLabel>Gender</ReadOnlyLabel>
                  <TextField fullWidth disabled value={gender} sx={disabledFieldSx} />
                </Box>
              </Box>
            </Paper>

          </Stack>
        </form>
      )}

      {/* Toast notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontFamily: 'Poppins', fontSize: '15px', borderRadius: '10px' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
